import store from '@/store.js'

// Una capa se vuelve a pedir en cada desplazamiento o cambio de escala, y entre una
// petición y la siguiente hay un instante sin nada en vuelo. Sin esta espera el aviso
// se prendería y apagaría en cada arrastre del mapa.
export const OFF_DELAY_MS = 220

// Red de protección: si una petición nunca informa que terminó, el contador jamás
// vuelve a cero y el aviso se queda encendido. Pasado este tiempo sin novedades se da
// la carga por cerrada. Es preferible dejar de avisar de más que avisar para siempre.
export const STUCK_TIMEOUT_MS = 15000

const IMAGE_EVENTS = ['imageloadstart', 'imageloadend', 'imageloaderror']
const TILE_EVENTS = ['tileloadstart', 'tileloadend', 'tileloaderror']
const VECTOR_EVENTS = ['featuresloadstart', 'featuresloadend', 'featuresloaderror']

function reportToStore({ layerId, loading, progress }) {
    store.dispatch('setLayerLoadState', { layerId, loading, progress })
}

// Esperas previas a que exista la fuente, por capa: verificar que el servicio ArcGIS
// responda o preguntarle al backend si la capa es ráster. Se guardan para poder
// cancelarlas cuando la carga real toma el relevo.
const pendingStarts = new Map()

function clearPendingStart(layerId) {
    const timer = pendingStarts.get(layerId)
    if (timer) {
        clearTimeout(timer)
        pendingStarts.delete(layerId)
    }
}

/**
 * Marca la capa como cargando desde que se pide encenderla. Antes de que exista la
 * fuente hay consultas al servicio que pueden tardar varios segundos, y sin esto el
 * usuario no veía nada hasta que aparecía el resultado.
 */
export function beginLayerLoad({ layerId, report = reportToStore }) {
    report({ layerId, loading: true, progress: null })
    clearPendingStart(layerId)
    pendingStarts.set(layerId, setTimeout(() => {
        pendingStarts.delete(layerId)
        report({ layerId, loading: false, progress: null })
    }, STUCK_TIMEOUT_MS))
}

/** Cierra la espera previa cuando la capa no llegó a agregarse. */
export function endLayerLoad({ layerId, report = reportToStore }) {
    clearPendingStart(layerId)
    report({ layerId, loading: false, progress: null })
}

/**
 * Sigue la carga de una fuente de OpenLayers y reporta si la capa está esperando datos.
 * No hay porcentaje posible: el servidor entrega imágenes ya renderizadas, así que el
 * progreso siempre viaja como null (barra indeterminada).
 *
 * Devuelve una función para soltar la capa, que desengancha los escuchas y apaga el aviso.
 */
function attachLoadTracking(source, { layerId, report, events, onFirstLoad }) {
    const [startEvent, endEvent, errorEvent] = events
    let pending = 0
    let offTimer = null
    let stuckTimer = null
    // El aviso de "capa lista" es solo para la primera carga: la capa se vuelve a pedir
    // en cada desplazamiento, y repetirlo ahí sería un cartel en cada arrastre del mapa.
    let announced = false
    let failed = false

    const clearTimers = () => {
        if (offTimer) { clearTimeout(offTimer); offTimer = null }
        if (stuckTimer) { clearTimeout(stuckTimer); stuckTimer = null }
    }

    const finish = () => {
        clearTimers()
        clearPendingStart(layerId)
        pending = 0
        report({ layerId, loading: false, progress: null })
    }

    const armStuckTimer = () => {
        if (stuckTimer) clearTimeout(stuckTimer)
        stuckTimer = setTimeout(finish, STUCK_TIMEOUT_MS)
    }

    const onLoadStart = () => {
        // La carga real toma el relevo de la espera previa: su límite ya no aplica.
        clearPendingStart(layerId)
        pending++
        if (offTimer) { clearTimeout(offTimer); offTimer = null }
        armStuckTimer()
        if (pending === 1) {
            report({ layerId, loading: true, progress: null })
        }
    }

    const onLoadSettled = () => {
        // Nunca bajar de cero: si llegan más finales que inicios (por ejemplo al
        // reengancharse a una fuente que ya venía cargando) el contador quedaría
        // negativo y la próxima carga no se anunciaría.
        pending = Math.max(0, pending - 1)
        if (pending > 0) {
            armStuckTimer()
            return
        }
        clearTimers()
        offTimer = setTimeout(() => {
            offTimer = null
            report({ layerId, loading: false, progress: null })
            // Solo se anuncia una carga completa y sin fallas; si algo falló, del aviso
            // de error se encarga quien corresponde y decir "lista" sería mentir.
            if (!announced && !failed && onFirstLoad) {
                announced = true
                onFirstLoad()
            }
            failed = false
        }, OFF_DELAY_MS)
    }

    const onLoadError = () => {
        failed = true
        onLoadSettled()
    }

    source.on(startEvent, onLoadStart)
    source.on(endEvent, onLoadSettled)
    source.on(errorEvent, onLoadError)

    return function detach() {
        source.un(startEvent, onLoadStart)
        source.un(endEvent, onLoadSettled)
        source.un(errorEvent, onLoadError)
        // Apagar explícitamente: una capa que se quita mientras carga dejaba el aviso encendido.
        finish()
    }
}

// Avisa una sola vez que la capa quedó lista, al encenderla.
function announceLayerReady(layerName) {
    store.dispatch('toast/show', {
        message: layerName ? `Capa "${layerName}" cargada` : 'Capa cargada',
        type: 'success'
    })
}

function defaultFirstLoad(layerName) {
    return () => announceLayerReady(layerName)
}

// Capas que llegan como una sola imagen: GeoServer WMS y ArcGIS ImageServer.
export function attachRasterLoadTracking(source, { layerId, layerName, report = reportToStore, onFirstLoad = defaultFirstLoad(layerName) }) {
    return attachLoadTracking(source, { layerId, report, onFirstLoad, events: IMAGE_EVENTS })
}

// Capas que llegan en mosaicos: ArcGIS MapServer y GeoServer con TILED.
export function attachTileLoadTracking(source, { layerId, layerName, report = reportToStore, onFirstLoad = defaultFirstLoad(layerName) }) {
    return attachLoadTracking(source, { layerId, report, onFirstLoad, events: TILE_EVENTS })
}

// Capas que descargan sus datos: ArcGIS FeatureServer y demás fuentes vectoriales.
export function attachVectorLoadTracking(source, { layerId, layerName, report = reportToStore, onFirstLoad = defaultFirstLoad(layerName) }) {
    return attachLoadTracking(source, { layerId, report, onFirstLoad, events: VECTOR_EVENTS })
}
